import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { NotificationService } from '../../services/notification.service';
import { TranslateService } from '@ngx-translate/core';
import { RecipeService } from '../../services/recipe/recipe.service';
import { RecipeLite } from '../../shared/models/recipe/recipe-lite.model';
import { RefDataService } from '../../services/ref-data.service';
import { Observable, of } from 'rxjs';
import { shareReplay, switchMap } from 'rxjs/operators';
import { DataRefsByKey, Unit } from '../../shared/models/dataref.model';
import { HttpErrorResponse } from '@angular/common/http';
import { RecipeForm } from '../../shared/models/recipe/recipe-form.model';
import { RecipeValidators } from './recipe.validators';
import { BINARY_TARGET_DOMAIN } from '@packs/utils/pack.utils';

@Component({
  selector: 'app-recipes-form',
  templateUrl: './recipes-form.component.html',
  styleUrls: ['./recipes-form.component.scss'],
  standalone: false,
})
export class RecipesFormComponent implements OnInit {
  // TODO copier form recette de ugc
  formRecipe: FormGroup<RecipeForm>;
  isEdit = false;
  recipeId = '';
  recipeType = '';
  recipeOriginal?: RecipeLite;
  isSubmitted = false;

  yieldUnits$?: Observable<DataRefsByKey<Unit>>;

  constructor(
    private formBuilder: FormBuilder,
    private untypedFormBuilder: UntypedFormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    private recipeService: RecipeService,
    private notificationService: NotificationService,
    private translateService: TranslateService,
    private refDataService: RefDataService,
  ) {
    this.formRecipe = this.createRecipeForm();
  }

  ngOnInit(): void {
    this.route.params
      .pipe(
        switchMap((params): Observable<RecipeLite | undefined> => {
          if (params.recipeId) {
            this.recipeId =
              params.version && params.type ? encodeURIComponent(`/${params.version}/${params.type}/${params.recipeId}/`) : params.recipeId;
            this.recipeType = params.recipeType;
            this.isEdit = true;
            // get recipe
            return this.recipeService.getRecipe(this.recipeType + '/' + this.recipeId);
          } else {
            this.recipeId = '';
            this.isEdit = false;
            return of(undefined);
          }
        }),
      )
      .subscribe({
        next: (recipe: RecipeLite | undefined) => {
          if (recipe) {
            this.yieldUnits$ = this.refDataService.getByKey('units', true, recipe.domain, recipe.market, recipe.lang).pipe(shareReplay(1));
            this.recipeOriginal = recipe;
            this.patchFormWithRecipe(this.recipeOriginal);
          } else {
            this.yieldUnits$ = this.refDataService.getByKey('units', true).pipe(shareReplay(1));
          }
        },
        error: (e) => {
          if (e.status === 404) {
            this.router.navigate(['404']);
          }
        },
      });
  }

  save(): void {
    if (this.formRecipe.invalid || this.isSubmitted) {
      return;
    }

    this.isSubmitted = true;
    const recipe = this.recipeService.convertFormdataToRecipePlain(this.formRecipe);

    if (this.isEdit) {
      this.updateRecipe(recipe);
    } else {
      delete recipe.steps;
      this.recipeService.createRecipe(recipe).subscribe({
        next: (recipeCreated) => {
          this.isSubmitted = false;
          this.router.navigateByUrl('recipes/edit/' + recipeCreated.identifier.sourceSystem + '/' + recipeCreated.identifier.functionalId);
        },
        error: (err) => this.errorManager(err),
      });
    }
  }

  updateRecipe(recipe: Partial<RecipeLite>): void {
    this.recipeService.updateRecipe(recipe).subscribe({
      next: (recipeUpdated) => {
        this.isSubmitted = false;
        this.recipeOriginal = recipeUpdated;
        this.notificationService.sendSuccess(this.translateService.instant('RECIPE_UPDATED'), true);
        if (this.recipeOriginal?.domain === BINARY_TARGET_DOMAIN) {
          this.notificationService.sendSuccess(this.translateService.instant('BINARIES.INFO_MSG'), true);
        }
      },
      error: (err) => this.errorManager(err),
    });
  }

  errorManager(err: HttpErrorResponse): void {
    let errorText = null;
    if (err.status && err.status === 403) {
      errorText = this.translateService.instant('RECIPE_UPDATE_FORBIDDEN');
      this.notificationService.sendError(errorText);
    } else {
      this.notificationService.sendAPIError(err);
    }
    this.isSubmitted = false;
  }

  private initStep(): UntypedFormGroup {
    return this.untypedFormBuilder.group({
      applicationDescription: [null, Validators.required],
      applianceDescription: [null],
      useAppliance: [null],
      kitchenwares: [[]],
      ingredients: [[]],
      ingredientsWithoutIdentifier: [[]],
      programDuration: [0, Validators.min(0)],
      manualDuration: [0, Validators.min(0)],
      sequences: [],
    });
  }

  /**
   * Create and initialize a new RecipeForm with its default values
   * @private
   */
  private createRecipeForm(): FormGroup<RecipeForm> {
    return this.formBuilder.group({
      domain: new FormControl<string | null>(null),
      market: new FormControl<string | null>(null),
      lang: new FormControl<string | null>(null),
      isPremium: new FormControl<boolean>(false),
      title: new FormControl<string | null>(null, { validators: [Validators.required, Validators.minLength(4)] }),
      digitalAssets: new FormControl<string[]>([], { nonNullable: true }),
      shortTitle: new FormControl<string | null>(null),
      resourceMedias: new FormControl([], {
        nonNullable: true,
        validators: [Validators.required, RecipeValidators.resourceMediaIsValid],
      }),
      yieldQuantity: new FormControl<string | null>(null, { validators: [Validators.required] }),
      yieldUnit: new FormControl<string | null>(null, { validators: [Validators.required] }),
      ingredients: this.untypedFormBuilder.array([], Validators.required),
      steps: this.untypedFormBuilder.array([this.initStep()], Validators.compose([RecipeValidators.stepsUseAppliance])),
      courses: new FormControl<string[]>([], { nonNullable: true }),
      digitalAsset: new FormControl<string | null>(null),
      occasions: this.untypedFormBuilder.array([]),
      categories: this.untypedFormBuilder.array([]),
      tips: new FormControl<string>(''),
      summary: new FormControl<string>(''),
      durations: this.formBuilder.group({
        prepTime: new FormControl(0, { nonNullable: true, validators: [RecipeValidators.durationsAreValid] }),
        offApplianceCookingTime: new FormControl(0, { nonNullable: true, validators: [RecipeValidators.durationsAreValid] }),
        // TODO: delete the use of nullValidator as it does nothing.. maybe it should be replaced by a not null validator ?
        restTime: new FormControl(0, { validators: [Validators.nullValidator] }),
      }),
      kitchenwares: this.untypedFormBuilder.array([]),
      defaultApplianceGroup: new FormControl<string | null>(null),
      applianceGroups: new FormControl<string[]>([], { nonNullable: true }),
      status: new FormControl<string | null>(null),
      privacyLevel: new FormControl<string | null>(null),
      recipeType: new FormControl<string | null>(null),
      partner: new FormControl<string | null>(null),
      chef: new FormControl<string | null>(null),
      isReworked: new FormControl<boolean>(false),
      seoTitle: new FormControl<string | null>(null),
      seoUrl: new FormControl<string | null>(null),
      isAIGenerated: new FormControl<boolean | null>(false),
    });
  }

  /**
   * Patch controls of current form with data coming from the recipe
   * @param recipeOriginal
   * @private
   */
  private patchFormWithRecipe(recipeOriginal: RecipeLite): void {
    this.formRecipe.patchValue({
      domain: recipeOriginal.domain,
      market: recipeOriginal.market,
      lang: recipeOriginal.lang,
      title: recipeOriginal.title,
      shortTitle: recipeOriginal.shortTitle,
      isPremium: recipeOriginal.isPremium ?? false,
      digitalAsset: recipeOriginal.digitalAsset,
      digitalAssets: recipeOriginal.digitalAssets ?? [],
      resourceMedias: recipeOriginal.resourceMedias,
      courses: recipeOriginal.courses,
      partner: recipeOriginal.partner,
      chef: recipeOriginal.chef,
      isReworked: recipeOriginal.isReworked ?? false,
      seoTitle: recipeOriginal.seoTitle,
      seoUrl: recipeOriginal.seoUrl,
      isAIGenerated: recipeOriginal.isAIGenerated ?? false,
    });
  }
}
